/**
 * UploadProvider — hält den gesamten Upload-State + die Engine (gelifted aus
 * dem früheren UploadModal, Verhalten WERTGLEICH). Dauerhaft im Layout über
 * Modal/Toast gemountet → ein laufender Upload überlebt Minimieren-zu-Toast
 * UND SPA-Navigation (die XHRs leben hier, nicht in der View-Komponente).
 *
 * Engine-Vertrag unverändert (Retry-on-429 + 502/503/504/Netzabriss, p-limit
 * CONCURRENCY=3 ≤ Server-Semaphore). Neu ggü. der alten Engine:
 *  - **Abort/Cancel** (X bei laufendem Upload → Confirm → `xhr.abort()` aller
 *    aktiven XHRs). `cancelledRef` disambiguiert User-Abort (stop, status 0)
 *    von Netzabriss (retry, status 0). Vor jedem (Re-)Versuch + vor Queue-Start
 *    + nach jedem Backoff-`delay` geprüft.
 *  - **run-token** (`batchIdRef`): nach Cancel/Reopen feuern stale p-limit-
 *    Continuations (setSubmitting/Summary/Events) NICHT mehr (C3).
 *  - **submittingRef** synchron vor `setSubmitting(true)` (C4) — `requestClose`
 *    sieht den Submit sofort (kein useEffect-Lag → kein Direct-Close-Race).
 *  - **Batch-Snapshot** (C5): gequeuete Tasks lesen NIE live Provider-State.
 *
 * Der Cancel-Confirm-Dialog lebt HIER (C1), nicht in der Modal-View — weil
 * `requestClose` eine stabile ControlContext-Callback ist und das component-
 * lokale `useConfirm` an die Modal-Instanz gebunden wäre.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import pLimit from 'p-limit';
import { useToast } from '../lib/toast-context';
import { isFileReadable, FILE_CHANGED_MESSAGE } from '../lib/file-readable';
import { ASSETS_CHANGED, RECIPES_CHANGED } from '../lib/events';
import { previewNewName, type RenamePattern } from '../../shared/rename-pattern';
import type { FacetIdsInput } from '../../shared/vocab-types';
import {
  UploadControlContext,
  UploadEngineContext,
  type PendingFile,
  type UploadControlApi,
  type UploadEngineApi,
  type UploadFields,
  type UploadView,
} from '../lib/upload-context';
import { ConfirmDialog } from './ui/ConfirmDialog';

// ── Engine-Konstanten — WERTGLEICH mit der früheren UploadModal-Engine ────
// (Memory upload-502-deploy-restart.md): nur der Ort wechselt, nicht das Verhalten.

// Client-Fan-Out: N separate POST /api/assets, max CONCURRENCY parallel via
// p-limit. MUSS ≤ Server-Semaphore (middleware/upload-concurrency.ts →
// MAX_CONCURRENT_UPLOADS) sein. 3 = exakt das Server-Limit → kein Self-Collision.
const CONCURRENCY = 3;

// Retry-on-429 (Server-Concurrency-Gate, Retry-After Pflicht-Header) +
// 502/503/504 + Netzabriss (Deploy-Recreate). 4xx/500 failen sofort.
const MAX_UPLOAD_ATTEMPTS = 3;
const DEFAULT_RETRY_AFTER_MS = 10000;
const RETRYABLE_TRANSIENT_STATUS = new Set([0, 502, 503, 504]);
const TRANSIENT_RETRY_BASE_MS = 3000;

type UploadOutcome = { ok: true; assetId: string } | { ok: false; error: string };

type PostResult =
  | { ok: true; assetId: string }
  | { ok: false; error: string; status: number; retryAfterMs: number | null };

/**
 * delayInterruptible — Backoff-Wartezeit zwischen Retry-Versuchen, über die
 * `wakers`-Map (keyed by fileId) vorzeitig auflösbar. Ein per-Datei-Cancel WÄHREND
 * des Backoffs (es gibt dann KEINEN aktiven XHR zum Abbrechen) weckt den Timer
 * sofort → uploadSingle bailt am nächsten `aborted()`-Check, statt bis zum Timer-
 * Ende zu warten. Ohne das bliebe `submitting` bei der LETZTEN so abgebrochenen
 * Datei bis zu 10 s true (UI gelockt + Batch-Cancel-Confirm trotz leerer Queue,
 * Codex-R1 [P2]). Batch-`cancel()` weckt ebenfalls alle (sonst dangling Timer).
 */
function delayInterruptible(
  ms: number,
  fileId: string,
  wakers: Map<string, () => void>,
): Promise<void> {
  return new Promise((resolve) => {
    const done = (): void => {
      clearTimeout(timer);
      if (wakers.get(fileId) === done) wakers.delete(fileId);
      resolve();
    };
    const timer = setTimeout(done, ms);
    wakers.set(fileId, done);
  });
}

/**
 * postAssetOnce — ein einzelner POST /api/assets via XHR (onprogress + onabort).
 * Registriert seinen XHR in der `activeXhrs`-Map unter der `fileId`, deregistriert
 * ihn beim Settle → `cancel()` kann alle aktiven XHRs `.abort()`en UND ein per-Datei-
 * Cancel kann gezielt `activeXhrs.get(id)?.abort()`. Genau EIN aktiver XHR pro fileId
 * (der `uploadSingle`-Loop ist sequenziell: Versuch N settlet + `delete` BEVOR
 * Versuch N+1 `set`t). `xhr.onabort` resolved mit `status:0` (= RETRYABLE) → der
 * `aborted()`-Guard im Retry-Loop verhindert, dass ein User-Abort als „Netzabriss"
 * re-tried wird.
 */
function postAssetOnce(
  file: File,
  fields: UploadFields,
  onProgress: (pct: number) => void,
  activeXhrs: Map<string, XMLHttpRequest>,
  fileId: string,
): Promise<PostResult> {
  return new Promise((resolve) => {
    const form = new FormData();
    form.append('file', file);
    form.append('tags', JSON.stringify(fields.tags));
    form.append('produktIds', JSON.stringify(fields.facets.produktIds));
    form.append('verwendungIds', JSON.stringify(fields.facets.verwendungIds));
    if (fields.displayName) form.append('displayFilename', fields.displayName);
    if (fields.recipeId) form.append('recipeId', fields.recipeId);

    const xhr = new XMLHttpRequest();
    activeXhrs.set(fileId, xhr);
    const settle = (result: PostResult): void => {
      // Nur den eigenen Eintrag löschen — ein per-Datei-Cancel könnte den Key schon
      // entfernt (oder, theoretisch, ein neuer Versuch ersetzt) haben.
      if (activeXhrs.get(fileId) === xhr) activeXhrs.delete(fileId);
      resolve(result);
    };
    xhr.open('POST', '/api/assets');
    xhr.withCredentials = true;
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      const header = xhr.getResponseHeader('Retry-After');
      const parsed = header ? Number(header) : NaN;
      const retryAfterMs = Number.isFinite(parsed) && parsed >= 0 ? parsed * 1000 : null;
      try {
        const body = JSON.parse(xhr.responseText) as { assetId?: string; error?: string };
        if (xhr.status >= 200 && xhr.status < 300 && body.assetId) {
          settle({ ok: true, assetId: body.assetId });
        } else {
          settle({ ok: false, error: body.error ?? `HTTP ${xhr.status}`, status: xhr.status, retryAfterMs });
        }
      } catch {
        settle({ ok: false, error: `HTTP ${xhr.status}`, status: xhr.status, retryAfterMs });
      }
    };
    xhr.onerror = () => settle({ ok: false, error: 'Netzwerkfehler', status: 0, retryAfterMs: null });
    xhr.onabort = () => settle({ ok: false, error: 'Abgebrochen', status: 0, retryAfterMs: null });
    xhr.send(form);
  });
}

/**
 * uploadSingle — Retry-Loop für eine Datei. Zwei Cancel-Achsen: batch
 * (`cancelledRef`) ODER per-Datei (`cancelledFileIds.has(fileId)`). Das zentrale
 * `aborted()`-Predicate wird VOR jedem Versuch UND nach jedem Backoff-`delay`
 * geprüft (C6 + per-File-Disambiguation) → bei Cancel sofortiges `{ok:false}` ohne
 * weiteren XHR / ohne auf den Timer zu warten. So bailt eine einzeln abgebrochene
 * Datei (status:0 vom abort), während andere Dateien echten Netzabriss weiter
 * re-tryen. `cancelledFileIds` ist das ROHE Set (Provider übergibt `.current`) —
 * `.add`/`.clear` über den Ref bleiben sichtbar (gleiche Instanz, in-place mutiert).
 */
async function uploadSingle(
  file: File,
  fields: UploadFields,
  onProgress: (pct: number) => void,
  activeXhrs: Map<string, XMLHttpRequest>,
  cancelledRef: { current: boolean },
  fileId: string,
  cancelledFileIds: Set<string>,
  wakers: Map<string, () => void>,
): Promise<UploadOutcome> {
  const aborted = (): boolean => cancelledRef.current || cancelledFileIds.has(fileId);
  for (let attempt = 1; attempt <= MAX_UPLOAD_ATTEMPTS; attempt++) {
    // Vor JEDEM Versuch prüfen: User-Abort (batch ODER per-Datei) darf nicht in
    // einen neuen XHR laufen.
    if (aborted()) return { ok: false, error: 'Abgebrochen' };
    // stale-File-Probe vor JEDEM Versuch (auch Retries): ein seit der Auswahl
    // geändertes/verschobenes File (z.B. Dropbox-Sync) ist deterministisch tot —
    // ohne die Probe verbrennt die transiente Ladder 3 Versuche mit dem
    // irreführenden „Netzwerkfehler" (siehe isFileReadable-Doku). Terminal, kein
    // Retry; der manuelle Retry-Button bleibt, falls der User die Datei fixt.
    if (!(await isFileReadable(file))) return { ok: false, error: FILE_CHANGED_MESSAGE };
    if (aborted()) return { ok: false, error: 'Abgebrochen' };
    const result = await postAssetOnce(file, fields, onProgress, activeXhrs, fileId);
    if (result.ok) return { ok: true, assetId: result.assetId };
    // status 0 + cancelled = User-Abort → NICHT re-tryen (Disambiguator).
    if (aborted()) return { ok: false, error: 'Abgebrochen' };
    if (attempt < MAX_UPLOAD_ATTEMPTS) {
      if (result.status === 429) {
        onProgress(0);
        await delayInterruptible(result.retryAfterMs ?? DEFAULT_RETRY_AFTER_MS, fileId, wakers);
        if (aborted()) return { ok: false, error: 'Abgebrochen' };
        continue;
      }
      if (RETRYABLE_TRANSIENT_STATUS.has(result.status)) {
        onProgress(0);
        await delayInterruptible(result.retryAfterMs ?? TRANSIENT_RETRY_BASE_MS * attempt, fileId, wakers);
        if (aborted()) return { ok: false, error: 'Abgebrochen' };
        continue;
      }
    }
    return { ok: false, error: result.error };
  }
  return { ok: false, error: 'Upload fehlgeschlagen' };
}

export function UploadProvider({ children }: { children: ReactNode }) {
  const toast = useToast();

  const [view, setView] = useState<UploadView>('closed');
  const [files, setFiles] = useState<PendingFile[]>([]);
  const [tagNames, setTagNames] = useState<string[]>([]);
  const [facets, setFacets] = useState<FacetIdsInput>({ produktIds: [], verwendungIds: [] });
  const [recipeId, setRecipeId] = useState<string | null>(null);
  const [recipeName, setRecipeName] = useState<string | null>(null);
  // recipeLocked: aus dem Rezept-Kontext geöffnet (open(recipe)) → Rezept fest.
  const [recipeLocked, setRecipeLocked] = useState(false);
  // Initialwerte für resetAll („Weitere hochladen" lädt weiter ins gelockte Rezept).
  const lockedRecipeRef = useRef<{ id: string; name: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [renameEnabled, setRenameEnabled] = useState(false);
  const [renamePattern, setRenamePattern] = useState<RenamePattern>({
    mode: 'sequence',
    base: '',
    start: 1,
    padding: 1,
  });
  const [renameMeaningful, setRenameMeaningful] = useState(false);

  // Cancel-Confirm-State lebt im Provider (C1), NICHT im Control-Context (sonst
  // re-rendern Control-Consumer beim Confirm-Toggle).
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);

  // ── Refs (Stale-Closure-frei) ─────────────────────────────────────────
  // activeXhrs: keyed by fileId → cancel() iteriert .values() (batch), ein
  // per-Datei-Cancel macht .get(id)?.abort() (gezielt). Genau 1 aktiver XHR/fileId.
  const activeXhrs = useRef<Map<string, XMLHttpRequest>>(new Map());
  const cancelledRef = useRef(false);
  // cancelledFileIds: per-Datei-Cancel-Achse neben dem batch-cancelledRef. Der
  // uploadSingle-Loop prüft `cancelledRef.current || cancelledFileIds.has(id)` →
  // eine einzeln abgebrochene Datei bailt, andere re-tryen echten Netzabriss weiter.
  // Geleert am handleSubmit-Start (frischer Batch) + in cancel(). uuid-ids → stale
  // Einträge harmlos, Clear hält den Set bounded.
  const cancelledFileIds = useRef<Set<string>>(new Set());
  // wakers: pro fileId ein „Backoff vorzeitig auflösen"-Callback (nur gesetzt während
  // uploadSingle in delayInterruptible wartet). removeFile/cancel wecken so eine
  // Datei, die zwischen Retries im Backoff hängt (kein aktiver XHR) — sonst bliebe
  // submitting bis zum Timer-Ende true (Codex-R1 [P2]).
  const wakers = useRef<Map<string, () => void>>(new Map());
  // submittingRef spiegelt `submitting` SYNCHRON (C4) — requestClose liest ihn,
  // nie den State (sonst Stale-Closure: requestClose sähe immer initiales false).
  const submittingRef = useRef(false);
  // run-token (C3): monoton steigend pro Submit. Stale Continuations bailen.
  const batchIdRef = useRef(0);
  // Der aktuelle p-limit-Limiter (C3): in einem Ref, damit `cancel()` die noch
  // gequeueten Tasks via `clearQueue()` hart abräumt (das `limit` selbst wird pro
  // Submit frisch erzeugt). Der `cancelledRef`-Guard in der Map ist die zweite
  // Schicht (falls ein Task schon dispatched war), batchId die dritte (Continuation).
  const limitRef = useRef<ReturnType<typeof pLimit> | null>(null);

  const handleRenameChange = useCallback((p: RenamePattern, m: boolean) => {
    setRenamePattern(p);
    setRenameMeaningful(m);
  }, []);

  // Rezept zugeordnet → Umbenennen automatisch an (Basisname = Rezeptname via
  // syncBase in RenamePatternFields). Feuert nur bei recipeName-Änderung.
  useEffect(() => {
    if (recipeName) setRenameEnabled(true);
  }, [recipeName]);

  // Geplante Anzeigenamen pro File (Index = Queue-Position). EINMAL berechnet →
  // identisch für Queue-Vorschau UND Upload (kein Drift).
  const renameActive = renameEnabled && renameMeaningful;
  const renamedNames = useMemo(() => {
    const m = new Map<string, string>();
    if (renameActive) {
      files.forEach((f, i) => m.set(f.id, previewNewName(renamePattern, f.file.name, i)));
    }
    return m;
  }, [files, renameActive, renamePattern]);

  const hasFacets = facets.produktIds.length > 0 && facets.verwendungIds.length > 0;
  const activeCount = files.filter((f) => f.status === 'uploading' || f.status === 'pending').length;

  // beforeunload-Warnung wenn noch aktive Uploads (lebt jetzt im Provider →
  // feuert auch im minimierten Toast-Zustand + bei SPA-Navigation weiter).
  useEffect(() => {
    if (activeCount === 0 || !submitting) return;
    const handler = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [activeCount, submitting]);

  const addFiles = useCallback((accepted: File[]): void => {
    const toAdd: PendingFile[] = accepted.map((f) => ({
      id: crypto.randomUUID(),
      file: f,
      status: 'pending',
      progress: 0,
    }));
    setFiles((prev) => [...prev, ...toAdd]);
  }, []);

  // „Universal cancel" — entfernt eine Datei aus der Queue, egal in welchem Status:
  //  - cancelledFileIds.add(id): ein noch nicht gestartetes p-limit-Task bailt am
  //    In-Task-Guard (schließt den pending-während-submitting-Gap); ein laufender
  //    Retry-Loop bailt am nächsten `aborted()` statt den abort als Netzabriss zu
  //    re-tryen.
  //  - aktiven XHR abbrechen, falls vorhanden (No-op für pending/error — Map leer).
  // Liest NICHT `files` (status-agnostisch) → keine Stale-Closure, deps bleiben [].
  // Fasst submitting/submittingRef NIE an — der submitting-Lifecycle gehört allein
  // handleSubmit (Batch) / retryFile (Einzel-Retry): ein Einzel-Cancel lässt die
  // übrigen Dateien (und damit submitting=true) unberührt.
  const removeFile = useCallback((id: string): void => {
    cancelledFileIds.current.add(id);
    const xhr = activeXhrs.current.get(id);
    if (xhr) {
      xhr.abort();
      activeXhrs.current.delete(id);
    }
    // Datei im Retry-Backoff (kein aktiver XHR): den delay-Timer wecken → uploadSingle
    // bailt sofort statt bis zum Timer-Ende zu warten. No-op wenn nicht im Backoff.
    wakers.current.get(id)?.();
    setFiles((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const setRecipe = useCallback((id: string | null, name: string | null) => {
    setRecipeId(id);
    setRecipeName(name);
  }, []);

  // VOLL-Reset des Client-States (R1-Punkt 5): files → []. Bei gelocktem Rezept
  // bleibt das Rezept gesetzt („Weitere hochladen" lädt weiter dort hinein).
  const resetAll = useCallback((): void => {
    setFiles([]);
    setTagNames([]);
    setFacets({ produktIds: [], verwendungIds: [] });
    const locked = lockedRecipeRef.current;
    setRecipeId(locked?.id ?? null);
    setRecipeName(locked?.name ?? null);
  }, []);

  // Ein einzelner Upload: Status→uploading, uploadSingle (inkl. Auto-Retry),
  // dann done/error. Von handleSubmit (Batch) UND retryFile geteilt.
  const uploadOne = useCallback(
    async (pf: PendingFile, fields: UploadFields): Promise<UploadOutcome> => {
      setFiles((prev) =>
        prev.map((f) =>
          f.id === pf.id ? { ...f, status: 'uploading', progress: 0, error: undefined } : f,
        ),
      );
      const result = await uploadSingle(
        pf.file,
        fields,
        (pct) => {
          setFiles((prev) => prev.map((f) => (f.id === pf.id ? { ...f, progress: pct } : f)));
        },
        activeXhrs.current,
        cancelledRef,
        pf.id,
        cancelledFileIds.current,
        wakers.current,
      );
      setFiles((prev) =>
        prev.map((f) =>
          f.id === pf.id
            ? result.ok
              ? { ...f, status: 'done', progress: 100, assetId: result.assetId }
              : { ...f, status: 'error', error: result.error }
            : f,
        ),
      );
      return result;
    },
    [],
  );

  const handleSubmit = useCallback(async (): Promise<void> => {
    if (!hasFacets) return;
    const pending = files.filter((f) => f.status === 'pending');
    if (pending.length === 0) return;

    // R1-Punkt 1: cancelledRef MUSS am Anfang zurückgesetzt werden, sonst failt
    // jeder Re-Open-Upload still am Guard (Ref ≠ State, resetAll fasst ihn nicht an).
    cancelledRef.current = false;
    // Frischer Batch → per-Datei-Cancel-Marker des vorigen Batches verwerfen.
    cancelledFileIds.current.clear();
    wakers.current.clear();
    // C4: submittingRef SYNCHRON vor setSubmitting — ein sofortiges Esc/X danach
    // sieht den aktiven Upload (kein useEffect-Lag → Confirm statt Direct-Close).
    submittingRef.current = true;
    setSubmitting(true);

    // C3: run-token capturen. Alle Post-await-Side-Effects prüfen ihn → stale
    // Continuations (nach Cancel/Reopen) sind no-ops.
    const myBatchId = ++batchIdRef.current;

    // C5: Batch-Metadaten zum Submit-Zeitpunkt EINFRIEREN. Gequeuete Tasks lesen
    // NUR den Snapshot — der User darf im minimierten Zustand Tags/Facetten/Rezept
    // editieren ohne die schon laufenden Dateien zu „mischen".
    const snapTags = tagNames;
    const snapFacets = facets;
    const snapRecipeId = recipeId;
    const snapRenamed = renamedNames;

    const limit = pLimit(CONCURRENCY);
    limitRef.current = limit;

    await Promise.all(
      pending.map((pf) =>
        limit(() => {
          // Guard VOR Queue-Start: (C3) batch-Abort der aktiven XHRs ODER (neu)
          // per-Datei-Cancel einer pending-Datei → das gequeuete Task macht keinen
          // POST mehr (schließt den pending-während-submitting-Gap).
          if (cancelledRef.current || cancelledFileIds.current.has(pf.id))
            return Promise.resolve({ ok: false, error: 'Abgebrochen' } as UploadOutcome);
          return uploadOne(pf, {
            tags: snapTags,
            facets: snapFacets,
            displayName: snapRenamed.get(pf.id),
            recipeId: snapRecipeId,
          });
        }),
      ),
    );

    // C3: stale Batch (Cancel/Reopen lief dazwischen) → kein Side-Effect mehr.
    if (myBatchId !== batchIdRef.current) return;

    submittingRef.current = false;
    setSubmitting(false);

    // Batch-Summary: kein Erfolgs-Toast wenn alles glatt lief (Queue zeigt ✓ pro
    // Datei). Bei Mixed/Fail ein klarer Hinweis. setTimeout damit setFiles oben
    // committed ist bevor wir den finalen Status lesen.
    const finalFiles = pending.map((p) => p.id);
    setTimeout(() => {
      // C3: auch der deferred Side-Effect prüft den run-token.
      if (myBatchId !== batchIdRef.current) return;
      setFiles((prev) => {
        const successCount = prev.filter(
          (f) => finalFiles.includes(f.id) && f.status === 'done',
        ).length;
        const failCount = prev.filter(
          (f) => finalFiles.includes(f.id) && f.status === 'error',
        ).length;
        if (failCount > 0) {
          toast.error(`${successCount} hochgeladen, ${failCount} fehlgeschlagen — siehe Liste unten.`);
        } else if (successCount > 0) {
          toast.success(`${successCount} ${successCount === 1 ? 'Asset' : 'Assets'} hochgeladen`);
        }
        if (successCount > 0) {
          window.dispatchEvent(new Event(ASSETS_CHANGED));
          if (snapRecipeId) window.dispatchEvent(new Event(RECIPES_CHANGED));
        }
        return prev;
      });
    }, 0);
  }, [hasFacets, files, tagNames, facets, recipeId, renamedNames, uploadOne, toast]);

  // Manueller per-Datei-Retry für eine fehlgeschlagene Datei. Fällt unter denselben
  // Cancel-Vertrag (C2): submittingRef synchron, cancelledRef-Reset, run-token.
  const retryFile = useCallback(
    async (id: string): Promise<void> => {
      const pf = files.find((f) => f.id === id);
      if (!pf || pf.status === 'uploading' || pf.status === 'done') return;
      if (!hasFacets) {
        toast.error('Produkt & Verwendung fehlen — bitte oben auswählen.');
        return;
      }
      cancelledRef.current = false;
      submittingRef.current = true;
      setSubmitting(true);
      const myBatchId = ++batchIdRef.current;
      const snapRecipeId = recipeId;
      try {
        const result = await uploadOne(pf, {
          tags: tagNames,
          facets,
          displayName: renamedNames.get(id),
          recipeId: snapRecipeId,
        });
        // C3: stale (Cancel/Reopen dazwischen) → keine Toast-/Event-Side-Effects.
        if (myBatchId !== batchIdRef.current) return;
        if (result.ok) {
          toast.success('Erneuter Upload erfolgreich');
          window.dispatchEvent(new Event(ASSETS_CHANGED));
          if (snapRecipeId) window.dispatchEvent(new Event(RECIPES_CHANGED));
        }
      } finally {
        // Nur der aktuelle Batch darf submitting zurücksetzen (sonst entsperrt ein
        // stale finally die UI mitten in einem neuen Flow).
        if (myBatchId === batchIdRef.current) {
          submittingRef.current = false;
          setSubmitting(false);
        }
      }
    },
    [files, hasFacets, tagNames, facets, renamedNames, recipeId, toast, uploadOne],
  );

  // ── Cancel — alle aktiven XHRs abort()en, State reset, Events dispatchen ──
  const cancel = useCallback((): void => {
    // C3/C6: cancelledRef + submitting SOFORT setzen → wartende Backoff-Continuations
    // bailen beim nächsten Check, der Close/Reset wartet NICHT auf den Timer.
    cancelledRef.current = true;
    // Batch-Cancel resettet auch alle per-Datei-Marker (cancelledRef dominiert hier
    // ohnehin, aber den Set bounded halten + sauberen Ausgangszustand garantieren).
    cancelledFileIds.current.clear();
    submittingRef.current = false;
    setSubmitting(false);
    // run-token invalidieren → die awaitende handleSubmit-Continuation ist stale.
    batchIdRef.current += 1;
    // C3: gequeuete (noch nicht gestartete) p-limit-Tasks hart abräumen, damit sie
    // gar nicht erst laufen (der cancelledRef-Guard in der Map fängt den Rest ab).
    limitRef.current?.clearQueue();

    // R1-Punkt 4 / C3: recipeId + done-count VOR resetAll snapshotten und Events
    // aus dem Snapshot dispatchen (sonst ist recipeId beim Check schon null).
    const snapRecipeId = recipeId;
    const doneCount = files.filter((f) => f.status === 'done').length;

    for (const xhr of activeXhrs.current.values()) {
      xhr.abort();
    }
    activeXhrs.current.clear();
    // Backoff-Waiter wecken (Dateien zwischen Retries, kein aktiver XHR) → ihre
    // uploadSingle-Loops bailen am cancelledRef sofort statt dangling Timer zu lassen.
    for (const wake of [...wakers.current.values()]) wake();
    wakers.current.clear();

    if (doneCount > 0) {
      window.dispatchEvent(new Event(ASSETS_CHANGED));
      if (snapRecipeId) window.dispatchEvent(new Event(RECIPES_CHANGED));
    }

    resetAll();
    lockedRecipeRef.current = null;
    setRecipeLocked(false);
    setRenameEnabled(false);
    setRenameMeaningful(false);
    setView('closed');
  }, [files, recipeId, resetAll]);

  // ── Control-Callbacks ─────────────────────────────────────────────────
  const open = useCallback((recipe?: { id: string; name: string }) => {
    // Open-while-active: läuft schon ein Upload im Toast → restoren, NICHT
    // clobbern (in-flight-Batch nicht verlieren; Recipe-Arg dann ignoriert).
    if (submittingRef.current) {
      setView('modal');
      return;
    }
    cancelledRef.current = false;
    if (recipe) {
      lockedRecipeRef.current = recipe;
      setRecipeLocked(true);
      setRecipeId(recipe.id);
      setRecipeName(recipe.name);
    } else {
      lockedRecipeRef.current = null;
      setRecipeLocked(false);
    }
    setView('modal');
  }, []);

  const minimize = useCallback(() => setView('toast'), []);
  const restore = useCallback(() => setView('modal'), []);

  // X/Esc/Backdrop einheitlich: submitting → Confirm (liest submittingRef, nie
  // den State → kein Stale-Closure, R1-Punkt 2); sonst close + reset ohne Confirm.
  const requestClose = useCallback(() => {
    if (submittingRef.current) {
      setConfirmCancelOpen(true);
      return;
    }
    cancelledRef.current = false;
    resetAll();
    lockedRecipeRef.current = null;
    setRecipeLocked(false);
    setRenameEnabled(false);
    setRenameMeaningful(false);
    setView('closed');
  }, [resetAll]);

  const confirmCancel = useCallback(() => {
    setConfirmCancelOpen(false);
    cancel();
  }, [cancel]);

  const dismissCancelConfirm = useCallback(() => setConfirmCancelOpen(false), []);

  // ── Context-Values ────────────────────────────────────────────────────
  // Control: STABIL via useMemo, ändert nur bei view-Wechsel (MH 10 — Topbar
  // re-rendert nicht pro Progress-Tick). Alle Callbacks sind stabile Identities.
  const controlValue = useMemo<UploadControlApi>(
    () => ({ view, open, minimize, restore, requestClose }),
    [view, open, minimize, restore, requestClose],
  );

  const engineValue = useMemo<UploadEngineApi>(
    () => ({
      files,
      tagNames,
      setTagNames,
      facets,
      setFacets,
      recipeId,
      recipeName,
      recipeLocked,
      setRecipe,
      renameEnabled,
      setRenameEnabled,
      handleRenameChange,
      renamedNames,
      submitting,
      addFiles,
      removeFile,
      resetAll,
      handleSubmit,
      retryFile,
    }),
    [
      files,
      tagNames,
      facets,
      recipeId,
      recipeName,
      recipeLocked,
      setRecipe,
      renameEnabled,
      handleRenameChange,
      renamedNames,
      submitting,
      addFiles,
      removeFile,
      resetAll,
      handleSubmit,
      retryFile,
    ],
  );

  return (
    <UploadControlContext.Provider value={controlValue}>
      <UploadEngineContext.Provider value={engineValue}>
        {children}
        {/* Cancel-Confirm auf Provider-Ebene (C1) — entkoppelt von der Modal-View. */}
        <ConfirmDialog
          open={confirmCancelOpen}
          onClose={dismissCancelConfirm}
          onConfirm={confirmCancel}
          title="Upload abbrechen?"
          message="Upload läuft noch. Bereits fertige Dateien bleiben erhalten."
          confirmLabel="Upload abbrechen"
          cancelLabel="Weiter"
          variant="danger"
        />
      </UploadEngineContext.Provider>
    </UploadControlContext.Provider>
  );
}
